'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

/**
  DelegatedIntersectionObserver
  A wrapper around a single IntersectionObserver that allows callbacks to be
  registered per target, at the point on observing a target. Implements the full
  IntersectionObserver, but moves `callback` from constructor to `observe`.
  >> If you need to observe multiple elements, it is both possible and advised
  >> to observe multiple elements using the same IntersectionObserver instance
  >> by calling observe() multiple times.
  -- https://developers.google.com/web/updates/2016/04/intersectionobserver
  It's up to calling code to construct a singleton with the specific options
  required. Eg, for lazy loading this may be a `rootMargin` value to load in
  advance, but would have to be the same for all lazy loadings using that instance.
  It's a trade off of configurability per entry, vs performance of multiple
  IntersectionObservers. Especially for a polyfill using scroll events
*/

var DelegatedIntersectionObserver = function () {
  function DelegatedIntersectionObserver(options) {
    _classCallCheck(this, DelegatedIntersectionObserver);

    this.targetCallbackPairs = [];

    this.intersectionObserver = new IntersectionObserver(this.intersectionCallback.bind(this), options);
  }

  _createClass(DelegatedIntersectionObserver, [{
    key: 'intersectionCallback',
    value: function intersectionCallback(entries) {
      var _this = this;

      // Fire the callback for any targetCallbackPairs where the target element
      // matches an IntersectionObserverEntry target element
      entries.forEach(function (entry) {
        _this.targetCallbackPairs.forEach(function (_ref) {
          var _ref2 = _slicedToArray(_ref, 2),
              target = _ref2[0],
              callback = _ref2[1];

          if (target === entry.target) {
            callback(entry);
          }
        });
      });
    }
  }, {
    key: 'observe',
    value: function observe(target, callback) {
      // Store the mappings as a two item array. An object of target => callback
      // doesn't work due to a lack of unique identifier for the target element.
      this.targetCallbackPairs.push([target, callback]);
      this.intersectionObserver.observe(target);
    }
  }, {
    key: 'unobserve',
    value: function unobserve(targetToUnobserve) {
      var isTargetObserved = false;

      // Stop observing and cleanup any targetCallbackPairs for this target
      this.targetCallbackPairs = this.targetCallbackPairs.filter(function (pair) {
        var _pair = _slicedToArray(pair, 1),
            target = _pair[0];

        // keep note of the fact that we were observing this target


        if (targetToUnobserve === target) {
          isTargetObserved = true;
        }

        return targetToUnobserve !== target;
      });

      if (isTargetObserved) {
        this.intersectionObserver.unobserve(targetToUnobserve);
      }
    }
  }, {
    key: 'disconnect',
    value: function disconnect() {
      this.intersectionObserver.disconnect();
    }
  }, {
    key: 'takeRecords',
    value: function takeRecords() {
      this.intersectionObserver.takeRecords();
    }
  }]);

  return DelegatedIntersectionObserver;
}();

/**
   Method used as a factory to create a lazy loader
   BE WARY OF PERFORMANCE when not using the singleton. This may become a problem as you scale up instances
   of IntersectionObservers and/or the number of observed objects due to the callbacks invoked when an
   intersection is observed and the associated CPU cycles
   https://developers.google.com/web/updates/2016/04/intersectionobserver

   Wraps DelegatedIntersectionObserver and provides a singleton instance, to
   avoid multiple instances of IntersectionObserver on the same page.
   Provides an observe helper method to avoid boilerplate code when observering
   a target, triggering the callback only when the target intersects the page,
   and automatically unobserving it before.
   So the behaviour of `LazyLoad().observe` is to fire once, and only once,
   when a target interesects the page. `LazyLoad.unobserve` is only required if
   for a target that has yet to intersect, eg, in a React `componentWillUnmount`
   For a usage example see `BackgroundImage`
*/

var createLazyLoader = exports.createLazyLoader = function createLazyLoader() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  // In server render `window` won't be defined, but this needs to be exported
  var isServer = typeof window === 'undefined';

  // Test browsers for InteresectionObserver support
  var isSupported = !isServer && window.IntersectionObserver;

  if (!isSupported) {
    return {
      isSupported: false
    };
  }

  // Add a 20% margin around the visible document that counts as visible, to allows
  // content that is about to come into view to trigger loading before it does
  var intersectionObserver = new DelegatedIntersectionObserver(_extends({
    rootMargin: '20%'
  }, options));

  return {
    isSupported: true,

    observe: function observe(target, callback) {
      // Trigger the callback only when interesecting and clear the observer
      intersectionObserver.observe(target, function (entry) {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          intersectionObserver.unobserve(target);
          callback();
        }
      });
    },

    unobserve: function unobserve(target) {
      intersectionObserver.unobserve(target);
    }
  };
};

/**
 The default behaviour is to return the global/a singleton for performance reasons
 */
exports.default = createLazyLoader();